import {
  GlobalButton,
  DateInput,
  SelectInput,
  TextInput,
  AutocompleteInput,
  AutocompleteMultiInput,
} from '@/components';
import { useState } from 'react';
import { Alert, Collapse } from '@mui/material';
import { GlobalButtonVariant } from '@/components/buttons/GlobalButton';
import {
  Search as SearchIcon,
  DeleteForever as TrashIcon,
  FileCopy as ExportIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import { RoutesFilter } from '../models';
import { RouteStatus, RouteStatusLabels } from '../enums';
import { useRoutesSearchBar, useRouteVisitsExport } from '../hooks';
import {
  getScheduledDateRangeValidationMessage,
  getScheduledExportRangeValidationMessage,
} from '../utils';
import { CircularProgress } from '@mui/material';

interface RouteSearchBarProps {
  initialFilter: RoutesFilter;
  filter: RoutesFilter;
  search: (appliedRoutesFilter: RoutesFilter) => void;
  mergeRoutesFilter: (partialRoutesFilter: Partial<RoutesFilter>) => void;
}

const routeStatusOptions = Object.values(RouteStatus).map((status) => ({
  label: RouteStatusLabels[status],
  value: status,
}));

const RouteSearchBar = ({
  initialFilter,
  filter,
  search,
  mergeRoutesFilter,
}: RouteSearchBarProps) => {
  const {
    localFilter,
    logisticOperatorInput,
    setLogisticOperatorInput,
    driverInput,
    setDriverInput,
    commerceInput,
    setCommerceInput,
    selectedLogisticOperator,
    selectedCommerce,
    logisticOperatorOptions,
    driverAutocompleteOptions,
    commerceOptions,
    isLogisticOperatorsLoading,
    isDriversLoading,
    isCommercesLoading,
    routeNameOptions,
    isRouteNamesLoading,
    routeNameInputValue,
    handleRouteNameInputChange,
    handleRouteNameOptionSelect,
    handleShippingNumberChange,
    handleRouteIdChange,
    handleLogisticOperatorSelect,
    handleDriverIdsChange,
    handleCommerceSelect,
    handleSearch,
    resetFilters,
    handleStatusChange,
    handleDateFromChange,
    handleDateToChange,
    dateRangeValidationMessage,
  } = useRoutesSearchBar({
    initialFilter,
    filter,
    search,
    mergeRoutesFilter,
  });

  const { handleExport, isExporting } = useRouteVisitsExport();

  const exportRangeLimitMessage = getScheduledDateRangeValidationMessage(
    localFilter
  )
    ? null
    : getScheduledExportRangeValidationMessage(localFilter);

  const hasRouteId = !!localFilter.routeId?.trim();
  const activeExtraFiltersCount = [
    localFilter.routeId,
    localFilter.routeName,
    localFilter.status,
  ].filter(Boolean).length;
  const [showMoreFilters, setShowMoreFilters] = useState(
    activeExtraFiltersCount > 0
  );

  return (
    <div className='w-full p-5 flex flex-col justify-center bg-white rounded-lg shadow-md gap-4'>
      <h3 className='text-lg font-semibold text-gray-800'>
        Filtros de búsqueda
      </h3>
      <div>
        <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
          <TextInput
            name='route-shipping-number'
            placeholder='Número de envío'
            value={localFilter.shippingNumber ?? ''}
            onChange={handleShippingNumberChange}
            required={false}
          />
          <DateInput
            name='scheduled-from'
            placeholder='Fecha desde'
            value={
              localFilter.scheduledFrom
                ? new Date(localFilter.scheduledFrom)
                : null
            }
            onChange={handleDateFromChange}
            disabled={hasRouteId}
          />
          <DateInput
            name='scheduled-to'
            placeholder='Fecha hasta'
            value={
              localFilter.scheduledTo ? new Date(localFilter.scheduledTo) : null
            }
            onChange={handleDateToChange}
            disabled={hasRouteId}
          />
          <AutocompleteMultiInput
            name='route-driver-search'
            placeholder='Conductores'
            value={localFilter.driverIds ?? []}
            onChange={handleDriverIdsChange}
            inputValue={driverInput}
            onInputChange={setDriverInput}
            options={driverAutocompleteOptions}
            required={false}
            loading={isDriversLoading}
          />
          <AutocompleteInput
            name='route-logistic-operator-search'
            placeholder='Operador logístico'
            value={selectedLogisticOperator}
            inputValue={
              selectedLogisticOperator
                ? selectedLogisticOperator.label
                : logisticOperatorInput
            }
            onChange={handleLogisticOperatorSelect}
            onInputChange={setLogisticOperatorInput}
            options={logisticOperatorOptions}
            required={false}
            loading={isLogisticOperatorsLoading}
          />
          <AutocompleteInput
            name='route-commerce-search'
            placeholder='Comercio'
            value={selectedCommerce}
            inputValue={
              selectedCommerce ? selectedCommerce.label : commerceInput
            }
            onChange={handleCommerceSelect}
            onInputChange={setCommerceInput}
            options={commerceOptions}
            required={false}
            loading={isCommercesLoading}
          />
        </div>
        <Collapse in={showMoreFilters}>
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 pt-4'>
            <TextInput
              name='route-id'
              placeholder='ID de ruta'
              value={localFilter.routeId ?? ''}
              onChange={handleRouteIdChange}
              required={false}
            />
            <AutocompleteInput
              name='route-name-search'
              placeholder='Nombre de ruta'
              value={null}
              inputValue={routeNameInputValue}
              onChange={handleRouteNameOptionSelect}
              onInputChange={handleRouteNameInputChange}
              options={routeNameOptions}
              required={false}
              loading={isRouteNamesLoading}
              freeSolo
              forcePopupIcon
            />
            <SelectInput
              name='route-status-filter'
              placeholder='Filtrar por estado'
              options={routeStatusOptions}
              value={localFilter.status || ''}
              onChange={(value) => handleStatusChange(String(value ?? ''))}
            />
          </div>
        </Collapse>
      </div>
      <div className='flex flex-wrap gap-2 justify-between items-center'>
        <GlobalButton
          variant={GlobalButtonVariant.textButton}
          action={() => setShowMoreFilters((previous) => !previous)}
        >
          {showMoreFilters ? (
            <KeyboardArrowUpIcon />
          ) : (
            <KeyboardArrowDownIcon />
          )}
          {showMoreFilters
            ? 'Ver menos'
            : activeExtraFiltersCount > 0
              ? `Ver más (${activeExtraFiltersCount})`
              : 'Ver más'}
        </GlobalButton>
        <div className='flex gap-2 justify-end items-center'>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={resetFilters}
            disabled={isExporting}
          >
            <TrashIcon />
            Limpiar
          </GlobalButton>
          <GlobalButton
            variant={GlobalButtonVariant.secondaryButton}
            action={() => {
              handleSearch();
              if (!getScheduledExportRangeValidationMessage(localFilter)) {
                handleExport(localFilter);
              }
            }}
            disabled={
              isExporting ||
              !!dateRangeValidationMessage ||
              !!exportRangeLimitMessage
            }
          >
            {isExporting ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              <ExportIcon color='primary' />
            )}
            Exportar
          </GlobalButton>
          <GlobalButton
            variant={GlobalButtonVariant.primaryButton}
            action={handleSearch}
            disabled={isExporting}
          >
            <SearchIcon />
            Buscar
          </GlobalButton>
        </div>
      </div>
      {dateRangeValidationMessage ? (
        <Alert severity='error'>{dateRangeValidationMessage}</Alert>
      ) : exportRangeLimitMessage ? (
        <Alert severity='info'>{exportRangeLimitMessage}</Alert>
      ) : hasRouteId ? (
        <Alert severity='info'>
          Al buscar por ID de ruta no se aplican las fechas.
        </Alert>
      ) : null}
    </div>
  );
};

export default RouteSearchBar;
